/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, inject, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, map, of, tap} from 'rxjs';
import {EsciMessageService} from '../../shared/esci-message.service';
import {environment} from '../../../environments/environment';
import {ColDef} from 'ag-grid-community';

@Component({
    selector: 'app-list-manager',
    templateUrl: './list-manager.component.html',
    styleUrls: ['./list-manager.component.scss'],
    standalone: false
})
export class ListManagerComponent { // extends EditableGridComponent {
    columns: ColDef[] = [
        {
            field: '', headerName: '', maxWidth: 40,
            cellRenderer: (params: any) => {
                const element = document.createElement('div');
                element.innerHTML = '<a href="javascript:void(0)" style="color: red; font-size: 1.2em;" ' +
                                       'class="fa fa-trash" title="Delete"></a>';
                element.querySelector('a').addEventListener('click', () => {
                    this.deleteListItem(params.data);
                });
                return element;
            }
        },
        {field: 'listItem', headerName: 'List Item Name', width: 245},
        {field: 'description', headerName: 'Description', width: 245, editable: true},
    ];

    /** Signal-backed so HTTP refresh after add/search does not trigger NG0100 on [rowData]. */
    readonly gridRowData = signal<{listItem: string; description: string | null}[]>([]);
    userMessage = '';
    // We use this value to determine if the component list dropdown should be disabled
    searchComponentListItemsUrl = `${environment.apiUrl}/esciUI/manage/componentListItems`;
    deleteComponentListItemsUrl = `${environment.apiUrl}/esciUI/delete/componentListItems`;
    saveComponentListItemsUrl   = `${environment.apiUrl}/esciUI/save/componentListItems`;
    saveListDescriptionRefUrl   = `${environment.apiUrl}/esciUI/save/componentListDescription`;
    getComponentTitlesUrl       = `${environment.apiUrl}/esciUI/getComponentTitles`;
    getListsUrl                 = `${environment.apiUrl}/esciUI/getLists`;
    maxNameLength = 1000;       // from ListValueTable.VALUE.columnSize;
    maxDescriptionLength = 255; // from ListValueTable.DESCRIPTION.columnSize;

    private readonly http = inject(HttpClient);
    private readonly messageService = inject(EsciMessageService);

    /** null = HTTP not completed yet; avoids showing empty-catalog copy before load. */
    private readonly catalogListsMapSignal = toSignal(
        this.http.get<Record<string, string>>(this.getListsUrl).pipe(
            map((componentLists) => new Map<string, string[]>(
                Array.from(Object.entries(componentLists)).map(([key, value]) => [key, value.split(' ')]),
            )),
            catchError(() => {
                this.messageService.processError('Failure to retrieve components list. Please contact an eSCI Admin.');
                return of(new Map<string, string[]>());
            }),
            tap(() => {
                this.dropdownComponentListSelection = {label: '', value: ''};
            }),
        ),
        {initialValue: null as Map<string, string[]> | null},
    );

    private readonly componentCatalogTitlesSignal = toSignal(
        this.http.get<string[]>(this.getComponentTitlesUrl).pipe(
            catchError(() => {
                this.messageService.processError('Failure to retrieve component titles. Please contact an eSCI Admin.');
                return of([] as string[]);
            }),
        ),
        {initialValue: null as string[] | null},
    );

    readonly componentCatalogTitles = computed(() => this.componentCatalogTitlesSignal() ?? []);

    readonly catalogListsBanner = computed(() => {
        const map = this.catalogListsMapSignal();
        if (map === null) {
            return '';
        }
        if (map.size < 1) {
            return 'There are no lists for this catalog.';
        }
        return '';
    });

    readonly dropdownComponentListMap = computed(() => {
        const map = this.catalogListsMapSignal();
        if (!map || map.size < 1) {
            return [] as { label: string; value: string }[];
        }
        const values = Array.from(map.keys())
            .map((key) => this.capitalizeWords(key))
            .sort((a, b) => a.localeCompare(b));
        return values.map((x) => ({label: x, value: x}));
    });

    readonly dropdownComponentListValue = computed(() => this.dropdownComponentListMap().map((o) => o.value));

    listType = '';
    // The value of the component catalog list dropdown
    dropdownComponentListSelection = {label: '', value: ''};
    isAdding = false;
    // The value of the item to be added to the component catalog list
    newItem = '';
    newItemValidationMessage = '';

    onToggleIsAdding = () => {
        this.isAdding = !this.isAdding;
    }

    // This method is called when the user selects a component catalog list from the dropdown
    onCellValueChanged = (event: any) => {
        const row = event.data;
        const desc = row.description ?? '';
        if (desc.length > this.maxDescriptionLength) {
            this.messageService.processError(`Description cannot have more than ${this.maxDescriptionLength} characters.`);
            return;
        }

        const parameters = {
            type: this.dropdownComponentListSelection.value.replace(/ /g, '_').toLowerCase().trim(),
            value: encodeURIComponent(row.listItem)
        };
        this.http.post(this.saveListDescriptionRefUrl, desc, {params: parameters})
            .subscribe({
                next: () => {
                    this.messageService.processSuccess('Description updated.');
                },
                error: () => {
                    this.messageService.processError('Failure to save list description. Please contact an eSCI Admin.');
                }
            });
    }

    isChecked = (title: string): boolean => {
        const map = this.catalogListsMapSignal();
        if (!map) {
            return false;
        }
        const values = map.get(this.listType);
        return values && values.indexOf(title.toLowerCase().replace(/ /g, '_')) !== -1;
    }

    // This method is will grab the results to display in the UI grid
    returnSearchResults = () => {
        this.clearSearch();
        this.newItem = '';
        if (this.dropdownComponentListValue().length === 0) {
            this.userMessage = 'There are no lists for this catalog.';
        } else {
            // Transform the component list name to the sql name of the catalog list
            // If the component catalog is multiple words, ex. Supplier Part Number -> supplier_part_number
            this.listType = this.dropdownComponentListSelection.value.replace(/ /g, '_').toLowerCase().trim();

            const parameters = {
                componentCatalogListName: this.listType
            };
            this.http.get(this.searchComponentListItemsUrl, {params: parameters})
                .subscribe({
                    next: (response: any) => {
                        this.convertResponseToData(response);
                    },
                    error: () => this.messageService.processError('Failure to get data. Please contact an eSCI Admin.')
                });
        }
    }

    convertResponseToData = (response: any) => {
        if (response.length === 0) {
            this.userMessage = 'No results found.';
            this.gridRowData.set([]);
        } else {
            const rows: {listItem: string; description: string | null}[] = [];
            response.forEach((entry: any) => {
                // The server returns a List<Pair<String, String>>, which are interpreted on the client side as {key, value} objects.
                rows.push({
                    listItem: entry.key,
                    description: entry.value ?? null,
                });
            });
            this.gridRowData.set(rows);
        }
    }

    // Clear out the contents of the UI grid and reset all messages
    clearSearch = () => {
        // Clear out the data on the grid and reset everything to the default values
        this.gridRowData.set([]);
        this.userMessage = '';
        this.newItem = '';
        // close the pop out for the add item
        this.isAdding = false;
    }

    // Create a new list item for the component catalog list
    createNewListItem = () => {
        this.userMessage = '';
        const key = this.dropdownComponentListSelection.value.toLowerCase().replace(/ /g, '_');
        const parameters = {
            components: this.catalogListsMapSignal()?.get(key),
            componentCatalogListName: this.listType,
            newListItem: this.newItem
        };

        this.http.get(this.saveComponentListItemsUrl, {params: parameters}).subscribe({
            next: () => {
                // return search results to display the new value
                this.returnSearchResults();
                // Confirm to the user that the new item was uploaded correctly
                this.messageService.processSuccess(`Item: '${parameters.newListItem}' has been successfully
                                           added to the ${this.dropdownComponentListSelection.label} list.`);
            },
            error: (response) => {
                const msg = response.error || response.data;
                if (msg != null) {
                    this.messageService.processError(`Error: ${JSON.stringify(msg)}`);
                } else {
                    this.messageService.processError('Error with creating new list item. Please contact an eSCI Admin.');
                }
                console.log(`Error: Creating new item failed with response: ${JSON.stringify(response)}`);
            }
        });
    }

    validateNewItem = () => {
        this.newItemValidationMessage = '';
        if (this.gridRowData().some((option) => option.listItem.toUpperCase() === this.newItem.toUpperCase())) {
            this.newItemValidationMessage = 'Duplicate item found.';
            return true;
        }
        if (this.newItem.length >= this.maxNameLength) {
            this.newItemValidationMessage = `New list item should be less than ${this.maxNameLength} characters.`;
            return true;
        }
        return false;
    }

    // Delete list item from the table
    deleteListItem = (row: any) => {
        const displayListItem = row.listItem;
        if (!confirm(`Are you sure you want to delete '${displayListItem}'?`)) {
            return;
        }
        const parameters = {
            listType: this.listType,
            listItem: row.listItem
        };

        this.http.get(this.deleteComponentListItemsUrl, {params: parameters})
            .subscribe({
                next: () => {
                    this.gridRowData.update((rows) => rows.filter((item) => item.listItem !== displayListItem));
                    this.messageService.processSuccess(`Item: '${displayListItem}' has been successfully
                                                         deleted from the ${this.dropdownComponentListSelection.label} list.`);
                },
                error: (response) => {
                    // Angular's HttpClient will wrap non-JSON error responses as:
                    // { error: SyntaxError, text: 'raw server message' }
                    const errorText = (typeof response?.error?.text === 'string') ? response.error.text : undefined;
                    const body = errorText ?? response.error ?? response.data;
                    if (typeof body === 'string') {
                        this.messageService.processError(body);
                    } else {
                        this.messageService.processError(
                            `Error deleting item '${displayListItem}' from list table. Please contact an eSCI Admin.`);
                    }
                }
            });
    }

    // Converts a string from something like "this_is_a_string" to "thisIsAString"
    convertToCamelCase = (inputString: string): string => {
        const parts = inputString.split('_');
        // Capitalize the first letter of each word except the first one.
        return parts.map((part, index) => {
            // Keep the first word as it is, but capitalize the first letter of subsequent words
            return (index === 0) ? part : part.charAt(0).toUpperCase() + part.slice(1);
        }).join('');
    }

    // Capitalize the first letter in each of the words to make it human-readable
    capitalizeWords = (originalWord: string): string => {
        originalWord = originalWord.replace(/_/g, ' ');
        return originalWord.replace(/\w\S*/g, function (capitalizedWord) {
            return capitalizedWord.charAt(0).toUpperCase() + capitalizedWord.substring(1).toLowerCase();
        });
    }

}
